import React, { useCallback, useState } from 'react';
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { IconButton } from 'ui-components/components/button/icon-button';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { PHONE_VERIFICATION_STATES } from '../../../../authentication/constants';
import { useCurrentUser } from '../../../../hooks/use-current-user';
import { generatePasswordResetLink } from '../../../../services/password-reset-link-generator';
import style from '../../style.module.css';
import ErrorBanner from '../components/error-banner';

const passwordType = 'password';
const textType = 'text';

/**
 * @typedef {import('../../api').StartStepUpAuthRes} StartStepUpAuthRes
 * @typedef {import('react-intl').MessageDescriptor} MessageDescriptor
 */
/**
 * @typedef {{ code: string, message: string }} ErrorObject
 */

/**
 * @param {object} props
 * @param {ErrorObject} [props.error]
 * @param {boolean} props.isDisabled
 * @param {boolean} [props.isUnskippable]
 * @param {Record<string, MessageDescriptor>} props.messages
 * @param {string} props.password
 * @param {(e: React.FormEvent<HTMLFormElement>) => void} props.onSubmit
 * @param {string} [props.phoneVerificationState]
 * @param {(arg0: ErrorObject) => void} props.setError
 * @param {(arg0: string) => void} props.setPassword
 * @returns {React.JSX.Element}
 */
export const PasswordPhaseBody = ({
  error,
  isDisabled,
  isUnskippable = false,
  messages,
  password,
  onSubmit,
  phoneVerificationState,
  setError,
  setPassword,
}) => {
  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onChange = (e) => {
    if (error) {
      setError(null);
    }
    setPassword(e.target.value);
  };

  const { username } = useCurrentUser();
  const passwordResetLink = generatePasswordResetLink(username);

  const [inputType, setInputType] = useState(passwordType);

  const togglePasswordInput = useCallback(() => {
    const newType = inputType === passwordType ? textType : passwordType;
    setInputType(newType);
  }, [inputType]);

  const getContextualMessage = () => {
    if (isUnskippable) {
      return messages.forcedMfaFlowContext;
    }
    if (phoneVerificationState === PHONE_VERIFICATION_STATES.MODAL) {
      return messages.secondSessionModalContext;
    }
    return null;
  };

  const contextualMessage = getContextualMessage();

  return (
    <FlexColumn className={style['password-challenge']} gap={Gap.Space16}>
      <Text textType={TextType.H3} value={messages.passwordTitle} />
      {contextualMessage ? <Text textType={TextType.Paragraph} value={contextualMessage} /> : null}
      <Text textType={TextType.Paragraph} value={messages.passwordSubheader} />
      {error ? <ErrorBanner code={error.code} message={error.message} /> : null}
      <Input
        className={style.username}
        disabled
        id='auth-username'
        label={<FormattedMessage {...messages.username} />}
        type='text'
        value={username}
        version={version2}
      />
      <form className={style['password-form']} onSubmit={onSubmit}>
        <Input
          disabled={isDisabled}
          focusOnMount
          id='auth-password'
          label={<FormattedMessage {...messages.password} />}
          onChange={onChange}
          type={inputType}
          value={password}
          version={version2}
        />
        <IconButton
          ariaLabelMessageDescriptor={messages.togglePasswordAriaLabel}
          className={style['password-visibility-toggle']}
          icon={inputType === passwordType ? faEye : faEyeSlash}
          onClick={togglePasswordInput}
        />
      </form>
      <Button href={passwordResetLink} target='_self' theme={THEME_CONSTANTS.link}>
        <FormattedMessage {...messages.forgotPassword} />
      </Button>
    </FlexColumn>
  );
};
